import ReactGA from "react-ga4";
import { whenFirstMapView } from "utils/firstMapView";

const GOOGLE_ANALYTICS_ID = process.env.REACT_APP_GOOGLE_ANALYTICS_ID;
// eslint-disable-next-line no-var
var initialized = false;
let initScheduled = false;
let warnedMissingId = false;
// GA calls made before ReactGA.initialize, run in order right after it.
const pendingCalls = [];

function hasGaId() {
  return Boolean(GOOGLE_ANALYTICS_ID && GOOGLE_ANALYTICS_ID !== "YOUR_API_KEY");
}

function runSafely(call) {
  try {
    call();
  } catch (error) {
    // Analytics must never break the user experience.
    console.warn("Analytics tracking failed:", error);
  }
}

function initializeNow() {
  ReactGA.initialize(GOOGLE_ANALYTICS_ID, {
    gtagOptions: { anonymize_ip: true },
  });
  initialized = true;
  while (pendingCalls.length > 0) runSafely(pendingCalls.shift());
}

class Analytics {
  // Runs `call` (which uses ReactGA) once GA is initialised. ReactGA.initialize
  // loads gtag.js (~180 KB), so it waits for the first map view instead of
  // competing with it; calls made before then are queued and sent in order
  // right after initialize. initialize's own config sends the page_view for
  // the page as it is then, as it did when gtag.js finished loading before.
  // Without a GA id, warns once and makes every log call a no-op instead of
  // throwing: the throw surfaced as a visible error on customizations without
  // analytics (e.g. after a successful map export).
  // eslint-disable-next-line class-methods-use-this
  _whenReady(call) {
    if (!hasGaId()) {
      if (!warnedMissingId) {
        warnedMissingId = true;
        console.warn("No Google Analytics ID available in the .env file");
      }
      return;
    }
    if (initialized) {
      runSafely(call);
      return;
    }
    pendingCalls.push(call);
    if (!initScheduled) {
      initScheduled = true;
      whenFirstMapView().then(() => runSafely(initializeNow));
    }
  }

  logPageView() {
    const page = window.location.pathname;
    this._whenReady(() => ReactGA.send({ hitType: "pageview", page }));
  }

  logEvent(category = "", action = "") {
    this._whenReady(() => {
      if (category && action) {
        ReactGA.event({ category, action });
      }
    });
  }

  logCustomizationEvent(action = "") {
    const customizationName =
      process.env.REACT_APP_CUSTOMIZATION_NAME || "earthmap";
    this._whenReady(() => {
      if (action) {
        ReactGA.event({
          category: "Customization Tracking",
          action,
          label: customizationName,
        });
      }
    });
  }

  setUserProperties() {
    const customizationName =
      process.env.REACT_APP_CUSTOMIZATION_NAME || "earthmap";
    this._whenReady(() =>
      ReactGA.set({
        customization: customizationName,
      }),
    );
  }

  logMapInitialized() {
    // Queued like any other call, so it never blocks the map's init.
    this._whenReady(() => {
      // Check if we've already logged this event in this session
      const sessionKey = "earthmap_session_map_initialized";
      const hasLoggedInSession = sessionStorage.getItem(sessionKey);

      if (!hasLoggedInSession) {
        ReactGA.event({
          category: "Map",
          action: "Map Initialized",
          label: "User Session Started",
        });
        // Mark that we've logged this event for this session
        sessionStorage.setItem(sessionKey, "true");
      }
    });
  }
}

export default Analytics;
